import { useCallback, useEffect, useState } from 'react'
import Icon from '../Icon'
import { api, go, useContent, type ClassDetail, type Grant, type Library, type Me } from '../../lib/classroom'
import { L, w } from '../../lib/words'
import { lessonTitle } from './StudentHome'
import { Sheet } from './Sheets'
import Pichi from './Pichi'
import { buzz, celebrate, sound } from '../../lib/fun'
import { validGrant, type Text } from '../../../shared/classroom'

export function joinUrl(code: string): string {
  return `${location.origin}/join/${code}`
}

/** The teacher's home: their classes, and a way to make a new one. */
export function TeacherHome({ me, refresh }: { me: Me; refresh: () => Promise<void> }) {
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const modules = useContent()

  const create = async () => {
    setError(null)
    try {
      const { class: c } = await api.createClass(name)
      setName('')
      await refresh()
      go(`/teach/${c.id}`)
    } catch (e) {
      setError((e as Error).message)
    }
  }

  return (
    <div>
      {me.live.map((r) => (
        <button key={r.id} className="live-banner" onClick={() => go(`/live/${r.id}`)}>
          <span className="pulse" />
          <span><strong>{w('backToLive')}</strong><br /><span className="small">{L(lessonTitle(modules, r.lesson_id))}</span></span>
          <Icon name="chevron" />
        </button>
      ))}
      <section className="card">
        <h1><Icon name="people" /> {w('classes')}</h1>
        <ul className="list">
          {me.teaching.map((c) => (
            <li key={c.id}>
              <button className="class-row" onClick={() => go(`/teach/${c.id}`)}>
                <strong>{c.name}</strong> <span className="muted small">{c.code}</span> <Icon name="chevron" />
              </button>
            </li>
          ))}
        </ul>
        <label>
          {w('newClass')}
          <div className="row">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={w('className')} />
            <button className="primary narrow" disabled={!name.trim()} onClick={create}><Icon name="plus" /> {w('create')}</button>
          </div>
        </label>
        {error && <p className="error">{error}</p>}
      </section>
      <button className="card lessons-link" onClick={() => go('/lecciones')}>
        <Pichi size={64} stage={2} mood="think" />
        <span><strong>{w('myLessons')}</strong><br /><span className="muted small">{w('createWithAi')} · {w('uploadLesson')} · {w('share')}</span></span>
        <Icon name="chevron" />
      </button>
    </div>
  )
}

/** One class: the join QR, the students, who needs a reteach, and the lessons to start. */
export function ClassView({ id }: { id: string }) {
  const modules = useContent()
  const [d, setD] = useState<ClassDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [student, setStudent] = useState<{ id: string; name: string } | null>(null)
  const [picking, setPicking] = useState(false)
  const load = useCallback(async () => {
    try { setD(await api.classDetail(id)) } catch (e) { setError((e as Error).message) }
  }, [id])
  useEffect(() => { void load() }, [load])

  if (error) return <p className="error card">{error}</p>
  if (!d) return <div className="boot">…</div>

  const live = d.runs.find((r) => !r.ended_at)
  const taught = new Set(d.runs.filter((r) => r.ended_at).map((r) => r.lesson_id))

  const start = async (lessonId: string) => {
    const { run } = await api.startRun(id, lessonId)
    go(`/live/${run.id}`)
  }

  return (
    <div>
      <section className="card class-head">
        <div>
          <h1>{d.class.name}</h1>
          <p className="code-big">{d.class.code}</p>
          <p className="small">{w('joinAt')} <b>{location.host}</b></p>
        </div>
      </section>

      {live && (
        <button className="live-banner" onClick={() => go(`/live/${live.id}`)}>
          <span className="pulse" /><strong>{w('backToLive')}</strong><Icon name="chevron" />
        </button>
      )}

      {d.reteach.length > 0 && (
        <section className="card reteach-card">
          <h2><Icon name="refresh" /> {w('needsReteach')}</h2>
          <ul className="list">
            {d.reteach.map((r) => (
              <li key={r.id}>
                <span><strong>{r.name}</strong> <span className="muted small">· {r.lesson_id} · {w('reteachWhy')}</span></span>
                <button onClick={async () => { await api.resolve(r.id); await load() }}><Icon name="check" size={16} /> {w('resolved')}</button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card">
        <h2><Icon name="play" /> {w('lessons')} <span className="muted small">{d.lessons.length}</span></h2>
        {d.lessons.length === 0 && <p className="muted">{w('classEmpty')}</p>}
        <ul className="lesson-grid">
          {d.lessons.map((l, i) => (
            <li key={l.id}>
              <div className={`lesson-card lesson-teach t${i % 8}`}>
                {taught.has(l.id) && <span className="tag">{w('taught')}</span>}
                <span className="lesson-num">{l.builtin ? l.id : i + 1}</span>
                <span className="lesson-name">{L(l.title)}</span>
                <span className="meta">{l.minutes} {w('minutes')} · {l.questions} {w('questions')}</span>
                <div className="actions">
                  <button onClick={() => void start(l.id)}><Icon name="play" size={16} /> {w('startLive')}</button>
                  <button aria-label={w('removeFromClassList')} title={w('removeFromClassList')} onClick={() => void api.removeLesson(id, l.id).then(load)}><Icon name="close" size={16} /></button>
                </div>
              </div>
            </li>
          ))}
        </ul>
        <button className="primary" onClick={() => setPicking(true)}><Icon name="plus" /> {w('addLessons')}</button>
      </section>

      {picking && <LessonPicker classId={id} inClass={d.lessons.map((l) => l.id)} onClose={() => { setPicking(false); void load() }} />}

      <section className="card">
        <h2><Icon name="people" /> {w('students')} <span className="muted small">{d.members.length}</span></h2>
        {d.members.length === 0 ? <p className="muted">{w('noStudents')}</p> : (
          <table className="table">
            <thead><tr><th>{w('students')}</th><th>{w('oroShort')}</th><th>{w('rightPct')}</th><th>{w('idk')}</th></tr></thead>
            <tbody>
              {d.members.map((m) => (
                <tr key={m.id} className="tap-row" onClick={() => setStudent({ id: m.id, name: m.name })}>
                  <td>{m.name} <span className="muted small">@{m.handle}</span></td>
                  <td>{m.xp}</td>
                  <td>{m.answered ? Math.round((m.right / m.answered) * 100) : 0}%</td>
                  <td>{m.idk}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {student && <StudentSheet classId={id} student={student} onClose={() => { setStudent(null); void load() }} />}

      <ClassTools classId={id} code={d.class.code} onChanged={load} />

      {d.runs.length > 0 && (
        <section className="card">
          <h2>{w('history')}</h2>
          <ul className="list">
            {d.runs.map((r) => (
              <li key={r.id}>
                <span>{r.lesson_id} · {L(lessonTitle(modules, r.lesson_id))}</span>
                <span className="muted small">{new Date(r.started_at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

/** Tap a student: verify the real-world things they showed you. */
function StudentSheet({ classId, student, onClose }: { classId: string; student: { id: string; name: string }; onClose: () => void }) {
  const [list, setList] = useState<{ key: string; title: Text; doneAt: number | null }[] | null>(null)
  const load = useCallback(async () => {
    setList((await api.studentMilestones(classId, student.id)).milestones)
  }, [classId, student.id])
  useEffect(() => { void load() }, [load])
  const toggle = async (key: string, done: boolean) => {
    // Unlock audio inside the tap itself — iOS refuses an AudioContext
    // started after an await, so the sound would never play.
    sound('tap')
    await api.verify(classId, student.id, key, done)
    if (done) { celebrate(); sound('level'); buzz([30, 40, 30]) }
    await load()
  }
  return (
    <Sheet open onClose={onClose} title={student.name}>
      <h2>{student.name}</h2>
      <h3>{w('verifyTitle')}</h3>
      <p className="muted small">{w('verifySub')}</p>
      <ul className="milestones">
        {list?.map((m) => (
          <li key={m.key} className={m.doneAt ? 'done' : ''}>
            <button className={`ms-toggle${m.doneAt ? ' on' : ''}`} aria-pressed={!!m.doneAt} onClick={() => void toggle(m.key, !m.doneAt)}>
              {m.doneAt && <Icon name="check" size={20} />}
            </button>
            <span className="ms-name">{L(m.title)}</span>
            <span className="ms-oro"><Icon name="coin" size={16} /> 250</span>
          </li>
        ))}
      </ul>
      <GiveXp classId={classId} studentId={student.id} />
      <RemoveStudent classId={classId} studentId={student.id} onDone={onClose} />
    </Sheet>
  )
}

/** Points given by hand — a prize, or a correction. The server holds the limit; this only asks. */
function GiveXp({ classId, studentId }: { classId: string; studentId: string }) {
  const [grants, setGrants] = useState<Grant[] | null>(null)
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => { void api.grants(classId, studentId).then((r) => setGrants(r.grants)) }, [classId, studentId])
  const xp = Number(amount)
  const give = async () => {
    setError(null)
    if (!validGrant(xp)) { setError(w('grantBadAmount')); return }
    sound('tap')
    setBusy(true)
    try {
      setGrants((await api.grant(classId, studentId, xp, reason.trim())).grants)
      setAmount('')
      setReason('')
      if (xp > 0) { celebrate(); sound('level'); buzz([30, 40, 30]) }
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <h3><Icon name="coin" /> {w('grantTitle')}</h3>
      <p className="muted small">{w('grantSub')}</p>
      <div className="row">
        <input type="number" inputMode="numeric" step={1} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={w('grantPoints')} aria-label={w('grantPoints')} />
        <input value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} placeholder={w('grantReason')} aria-label={w('grantReason')} />
        <button className="primary narrow" disabled={busy || !amount || !reason.trim()} onClick={() => void give()}><Icon name="plus" /> {w('grantGive')}</button>
      </div>
      {error && <p className="error">{error}</p>}
      {grants && grants.length > 0 && (
        <ul className="list">
          {grants.map((g) => (
            <li key={g.id}>
              <span><strong>{g.xp > 0 ? '+' : ''}{g.xp}</strong> {g.reason}</span>
              <span className="muted small">{g.by ? `${g.by} · ` : ''}{new Date(g.createdAt).toLocaleDateString()}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

/** Taking a student out of the class. Two taps, because it deletes their class answers. */
function RemoveStudent({ classId, studentId, onDone }: { classId: string; studentId: string; onDone: () => void }) {
  const [sure, setSure] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const remove = async () => {
    try { await api.removeStudent(classId, studentId); onDone() } catch (e) { setError((e as Error).message) }
  }
  return (
    <div className="danger-zone">
      <h3><Icon name="trash" /> {w('removeStudent')}</h3>
      <p className="muted small">{w('removeStudentBody')}</p>
      {!sure
        ? <button onClick={() => setSure(true)}>{w('removeStudent')}</button>
        : <button className="danger-fill" onClick={() => void remove()}><Icon name="trash" /> {w('confirm')}</button>}
      {error && <p className="error">{error}</p>}
    </div>
  )
}

/**
 * The class's own data: the gradebook export, PIN accounts for students who
 * cannot hold a passkey (a shared school computer, no phone of their own), and
 * deleting the class. What a district's privacy review asks a teacher to be
 * able to do, in one place.
 */
function ClassTools({ classId, code, onChanged }: { classId: string; code: string; onChanged: () => Promise<void> }) {
  const [name, setName] = useState('')
  const [handle, setHandle] = useState('')
  const [pin, setPin] = useState('')
  const [made, setMade] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [del, setDel] = useState(false)
  const [typed, setTyped] = useState('')

  const addPin = async () => {
    setBusy(true); setError(null); setMade(null)
    try {
      await api.addPinStudent(classId, { handle, displayName: name.trim(), pin })
      setMade(handle)
      setName(''); setHandle(''); setPin('')
      await onChanged()
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }
  const remove = async () => {
    setBusy(true); setError(null)
    try { await api.deleteClass(classId, typed); go('/') } catch (e) { setError((e as Error).message); setBusy(false) }
  }

  return (
    <section className="card">
      <h2><Icon name="shield" /> {w('classTools')}</h2>
      <p><a className="button" href={api.exportUrl(classId)} download><Icon name="download" /> {w('exportCsv')}</a></p>

      <h3><Icon name="lock" /> {w('addPinStudent')}</h3>
      <p className="muted small">{w('addPinStudentSub')}</p>
      <div className="pin-form">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={w('studentName')} aria-label={w('studentName')} />
        <input value={handle} onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/\s/g, ''))} placeholder={w('username')} aria-label={w('username')} autoCapitalize="none" autoCorrect="off" spellCheck={false} />
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 12))} placeholder={w('pinDigits')} aria-label={w('pinDigits')} inputMode="numeric" autoComplete="off" />
        <button className="primary narrow" disabled={busy || !name.trim() || !handle || pin.length < 6} onClick={() => void addPin()}><Icon name="plus" /> {w('create')}</button>
      </div>
      {made && <p className="small"><Icon name="check" size={16} /> {w('pinCreated')} <strong>@{made}</strong></p>}

      <div className="danger-zone">
        <h3><Icon name="trash" /> {w('deleteClass')}</h3>
        {!del ? <button onClick={() => setDel(true)}>{w('deleteClass')}</button> : (
          <>
            <p className="muted small">{w('deleteClassBody')}</p>
            <label>{w('typeCode')} <span className="muted small">({code})</span>
              <input value={typed} onChange={(e) => setTyped(e.target.value.toUpperCase())} autoCapitalize="characters" autoCorrect="off" spellCheck={false} />
            </label>
            <button className="danger-fill" disabled={busy || typed.trim() !== code} onClick={() => void remove()}><Icon name="trash" /> {w('deleteForever')}</button>
          </>
        )}
      </div>
      {error && <p className="error" role="alert">{error}</p>}
    </section>
  )
}

/**
 * Adding lessons to a class: the built-in library (a whole module or one
 * lesson at a time) and the teacher's own lessons that are marked ready.
 */
function LessonPicker({ classId, inClass, onClose }: { classId: string; inClass: string[]; onClose: () => void }) {
  const [lib, setLib] = useState<Library | null>(null)
  const [have, setHave] = useState<string[]>(inClass)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => { void api.library().then(setLib).catch((e) => setError((e as Error).message)) }, [])
  const add = async (ids: string[]) => {
    setError(null)
    try { const r = await api.addLessons(classId, ids); setHave(r.lessons.map((l) => l.id)) } catch (e) { setError((e as Error).message) }
  }
  const ready = lib?.lessons.filter((l) => l.status === 'ready') ?? []
  const row = (id: string, title: Text, meta: string) => (
    <li key={id}>
      <span><strong>{L(title)}</strong><br /><span className="muted small">{meta}</span></span>
      {have.includes(id)
        ? <span className="tag-in"><Icon name="check" size={14} /> {w('inClassTag')}</span>
        : <button className="narrow" onClick={() => void add([id])}><Icon name="plus" size={16} /></button>}
    </li>
  )
  return (
    <Sheet open onClose={onClose} title={w('addLessons')}>
      <h2>{w('addLessons')}</h2>
      {!lib && !error && <div className="boot">…</div>}
      {lib && (
        <>
          <h3><Icon name="book" /> {w('myLessons')}</h3>
          {ready.length === 0
            ? <p className="muted small">{w('noLessonsYet')} <button className="link" onClick={() => go('/lecciones')}>{w('createOrUpload')}</button></p>
            : <ul className="list">{ready.map((l) => row(l.id, l.title, `${l.minutes} ${w('minutes')} · ${l.questions} ${w('questions')}`))}</ul>}
          {lib.builtin.map((m) => (
            <div key={m.module.id}>
              <h3><Icon name="star" /> {w('library')}: {L(m.module.title)}</h3>
              <button onClick={() => void add(m.lessons.map((l) => l.id))}><Icon name="plus" size={16} /> {w('addAll')}</button>
              <ul className="list">{m.lessons.map((l) => row(l.id, l.title, `${l.id} · ${l.minutes} ${w('minutes')} · ${l.questions} ${w('questions')}`))}</ul>
            </div>
          ))}
          <p><button className="link" onClick={() => go('/lecciones')}><Icon name="sparkle" size={14} /> {w('createOrUpload')}</button></p>
        </>
      )}
      {error && <p className="error">{error}</p>}
      <button className="primary" onClick={onClose}>{w('done')}</button>
    </Sheet>
  )
}
